/*
 * --------------------------------------
 * Copyright (c) 2020 BetterCapital.US
 * --------------------------------------
 */

import { fork, all } from "redux-saga/effects";

import {
  watchLogin,
  watchSignup,
  watchForgotPassword,
  watchResetPassword,
  watchGoogleLogin,
  watchGetUserDetail,
  watchUpdateUserDetail,
  watchActivateUser,
  watchUpdatePassword,
  watchInvestorProfile,
  watchUpdateInvestorProfile,
  watchSaveFilter
} from "./authSagas";

import {
  watchAssetList,
  watchLookupAsset,
  watchLookupParcel,
  watchSaveAsset,
  watchAddAssetAndFinancials,
  watchAssetDetail,
  watchDeleteAsset,
  watchGetAssetBasicInfo,
  watchUpdateAssetBasicInfo,
  watchGetAssetInvestmentInfo,
  watchUpdateAssetInvestmentInfo,
  watchGetAccountDetail,
  watchGetAssetValuation,
  watchGetAssetCashflow,
  watchGetMonthwiseIncomes,
  watchGetMonthwiseExpenses,
  watchHasAsset,
  watchGetMonthlyOverallCashflow,
  watchGetTotalMonthlyStats,
  watchGetDashboardStats,
  watchGetAssetStats,
  watchSaveAssetNotFoundLog,
  watchExportToExcel,
  watchGetMonthlyGraph,
  watchGetYearlyGraph,
  watchUpdateIncomeGoal,
  watchGetAssetChecklist,
  watchGetCashFlowGraph,
  watchAddAssetMedias,
  watchGetAssetMedias,
  watchDeleteAssetMedia,
  watchGetMonthlySnapshot,
  watchBulkAddAsset,
  watchDownloadBulkAddCSV,
  watchGetAllDocuments,
  watchDeleteDocument,
  watchUpdateDocument,
  watchUploadDocuments,
  watchAddEntity,
  watchGetEntities,
  watchCloseAsset,
  watchRunReport
} from "./assetSagas";

import {
  watchGetAccounts,
  watchAddAccount,
  watchAddBankToAsset,
  watchRemoveBankFromAsset,
  watchRemoveAccount,
  watchAddAccountFilter,
  watchGetAccountFilters,
  watchImportBankTransaction
} from "./accountSagas";

import {
  watchGetTransactions,
  watchSaveTransactions,
  watchSaveTransaction,
  watchGetTransactionsFromBank,
  watchUpdateTransaction,
  watchDeleteTransaction,
  watchMultiDeleteTransaction,
  watchDownloadSampleCSV,
  watchRemoveAllDuplicates,
  watchMarkAllPaid,
  watchdownloadPastSampleXLSX,
  watchGetAllTransactions
} from "./transactionSagas";

import { watchResendEmail } from "./resendEmailSaga";
import { watchGetPolicyContent, watchSaveFeedback } from "./cmsSagas";

import {
  watchGetResources,
  watchReserveResource,
  watchGetResourceStats,
  watchPostResource,
  watchUploadFile,
  watchGetMyResources,
  watchGetMyResourceStats,
  watchGetViewResource
} from "./resourceSagas";

import {
  watchSaveAttachments,
  watchGetAttachments,
  watchGetAttachmentUrl,
  watchDeleteAttachment,
  watchAddAttachmentTags,
  watchRequestDocument
} from "./attachmentSagas";

import { watchGetNotificationList } from "./notificationSagas";
import {
  watchGetAssetContacts,
  watchAddAssetContact,
  watchDeleteAssetContact,
  watchUpdateAssetContact,
  watchGetAllContacts,
  watchAddContact,
  watchUpdateContact,
  watchDeleteContact
} from "./contactSagas";

import {
  watchAddFinancialToAsset,
  watchGetAssetFinancials,
  watchUpdateAssetFinancial,
  watchDeleteAssetFinancial
} from "./financialSagas";

import { watchGetRecommendations } from "./recommendationSagas";
import { watchSendInvite } from "./inviteSaga";
import {
  watchAddDeal,
  watchGetDeals,
  watchGetUserDeals,
  watchTransferAsset
} from "./dealSagas";
import { watchPropParser } from "./propParserSagas";

import {
  watchGetReminderList,
  watchAddReminder,
  watchUpdateReminder
} from "./reminderSagas";
import {
  watchGetPartner,
  watchUpdatePartner,
  watchSignupPartner,
  watchListPartners,
  watchSubmitTestimonial,
  watchGrantPermission
} from "./partnerSagas";

export default function* rootSaga() {
  yield all([
    fork(watchLogin),
    fork(watchSignup),
    fork(watchGoogleLogin),
    fork(watchForgotPassword),
    fork(watchResetPassword),
    fork(watchActivateUser),
    fork(watchAssetList),
    fork(watchLookupAsset),
    fork(watchLookupParcel),
    fork(watchSaveAsset),
    fork(watchAddAssetAndFinancials),
    fork(watchAssetDetail),
    fork(watchHasAsset),
    fork(watchGetMonthwiseIncomes),
    fork(watchGetMonthwiseExpenses),
    fork(watchGetMonthlySnapshot),
    fork(watchAddAssetMedias),
    fork(watchGetAssetMedias),
    fork(watchDeleteAssetMedia),
    fork(watchGetUserDetail),
    fork(watchUpdateUserDetail),
    fork(watchSaveFilter),
    fork(watchUpdatePassword),
    fork(watchInvestorProfile),
    fork(watchUpdateInvestorProfile),
    fork(watchDeleteAsset),
    fork(watchGetAccounts),
    fork(watchAddAccount),
    fork(watchAddBankToAsset),
    fork(watchRemoveBankFromAsset),
    fork(watchGetTransactions),
    fork(watchRemoveAccount),
    fork(watchAddAccountFilter),
    fork(watchGetAccountFilters),
    fork(watchResendEmail),

    fork(watchGetPolicyContent),
    fork(watchSaveFeedback),
    fork(watchGetResources),
    fork(watchGetRecommendations),

    fork(watchGetAttachments),
    fork(watchSaveAttachments),
    fork(watchGetAttachmentUrl),
    fork(watchDeleteAttachment),
    fork(watchAddAttachmentTags),
    fork(watchRequestDocument),
    fork(watchGetNotificationList),

    fork(watchSaveTransactions),
    fork(watchSaveTransaction),
    fork(watchGetTransactionsFromBank),

    fork(watchAddFinancialToAsset),
    fork(watchGetAssetFinancials),
    fork(watchUpdateAssetFinancial),
    fork(watchDeleteAssetFinancial),
    fork(watchGetAssetContacts),
    fork(watchAddAssetContact),
    fork(watchUpdateAssetContact),
    fork(watchDeleteAssetContact),
    fork(watchAddContact),
    fork(watchUpdateContact),
    fork(watchDeleteContact),

    fork(watchGetAssetBasicInfo),
    fork(watchUpdateAssetBasicInfo),

    fork(watchGetAssetInvestmentInfo),
    fork(watchUpdateAssetInvestmentInfo),

    fork(watchGetAccountDetail),
    fork(watchGetAssetValuation),
    fork(watchGetAssetCashflow),

    fork(watchUpdateTransaction),
    fork(watchDeleteTransaction),
    fork(watchMultiDeleteTransaction),

    fork(watchGetMonthlyOverallCashflow),
    fork(watchGetTotalMonthlyStats),
    fork(watchGetDashboardStats),
    fork(watchGetAssetStats),

    fork(watchSaveAssetNotFoundLog),
    fork(watchExportToExcel),
    fork(watchGetMonthlyGraph),
    fork(watchGetYearlyGraph),
    fork(watchGetCashFlowGraph),
    fork(watchDownloadSampleCSV),
    fork(watchdownloadPastSampleXLSX),
    fork(watchRemoveAllDuplicates),
    fork(watchMarkAllPaid),
    fork(watchGetAllContacts),
    fork(watchSendInvite),
    fork(watchAddDeal),
    fork(watchGetDeals),
    fork(watchReserveResource),
    fork(watchGetUserDeals),
    fork(watchUpdateIncomeGoal),
    fork(watchGetAssetChecklist),
    fork(watchTransferAsset),

    fork(watchGetResourceStats),
    fork(watchPostResource),
    fork(watchUploadFile),

    fork(watchGetMyResources),
    fork(watchGetMyResourceStats),

    fork(watchGetViewResource),
    fork(watchPropParser),

    fork(watchGetReminderList),
    fork(watchAddReminder),
    fork(watchUpdateReminder),

    fork(watchGetPartner),
    fork(watchSignupPartner),
    fork(watchUpdatePartner),
    fork(watchListPartners),
    fork(watchSubmitTestimonial),
    fork(watchGrantPermission),

    fork(watchBulkAddAsset),
    fork(watchDownloadBulkAddCSV),

    fork(watchGetAllDocuments),
    fork(watchDeleteDocument),
    fork(watchUpdateDocument),
    fork(watchUploadDocuments),
    fork(watchImportBankTransaction),
    fork(watchGetAllTransactions),
    fork(watchAddEntity),
    fork(watchGetEntities),
    fork(watchCloseAsset),
    fork(watchRunReport)
  ]);
}
